import api from './client';
import type { ApiError } from './client';

/**
 * Raw error shape returned by data-layer helpers in this module.
 *
 * `code` is either the backend-provided error code (e.g. `INTERNAL_ERROR`)
 * or a client-side fallback (e.g. `transcription_failed`). `raw` is the
 * original thrown error — callers pass it to `captureFriendlyError` so the
 * stack trace is preserved in Sentry.
 */
export interface ApiCallError {
  code: string;
  raw: unknown;
}

export interface TranscriptionResult {
  success: boolean;
  transcript?: string;
  confidence?: number;
  error?: ApiCallError;
}

export interface GradingResult {
  success: boolean;
  isCorrect?: boolean;
  correctAnswer?: string;
  error?: ApiCallError;
}

interface TranscriptionResponse {
  data?: {
    transcript?: string;
    confidence?: number;
  };
  transcript?: string;
  confidence?: number;
}

interface GradingResponse {
  data: {
    is_correct: boolean;
    user_answer: string;
    correct_answer?: string;
  };
  success: boolean;
}

/**
 * Transcribe audio via Deepgram API
 * @param audioBlob - Blob of the recorded audio
 * @param questionKeywords - Optional keywords to boost transcription accuracy
 * @returns TranscriptionResult with transcript or error
 */
export async function transcribeAudio(
  audioBlob: Blob,
  questionKeywords?: string[],
): Promise<TranscriptionResult> {
  try {
    const formData = new FormData();
    const extension = audioBlob.type.includes('webm') ? 'webm' : 'mp4';
    formData.append('audio', audioBlob, `recording.${extension}`);

    if (questionKeywords && questionKeywords.length > 0) {
      formData.append('questionKeywords', JSON.stringify(questionKeywords));
    }

    const result = await api.post<TranscriptionResponse>('/api/transcribe', formData);

    return {
      success: true,
      transcript: result.data?.transcript || result.transcript,
      confidence: result.data?.confidence || result.confidence,
    };
  } catch (error) {
    const apiError = error as ApiError;
    return {
      success: false,
      error: { code: apiError.code || 'transcription_failed', raw: error },
    };
  }
}

/**
 * Grade an answer via Claude API
 * @param questionId - The question ID
 * @param userAnswer - The user's answer text
 * @returns GradingResult with correctness or error
 */
export async function gradeAnswer(
  questionId: number,
  userAnswer: string,
): Promise<GradingResult> {
  try {
    const result = await api.post<GradingResponse>('/api/grade', {
      question_id: questionId,
      user_answer: userAnswer,
    });

    return {
      success: result.success,
      isCorrect: result.data?.is_correct,
      correctAnswer: result.data?.correct_answer || undefined,
    };
  } catch (error) {
    const apiError = error as ApiError;
    return {
      success: false,
      error: { code: apiError.code || 'grading_failed', raw: error },
    };
  }
}
